import React, { useRef, useState } from "react";
import { Play, Loader2 } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const VIDEO = {
    src: "/videos/how-it-works.mp4",
    srcMobile: "/videos/how-it-works-720.mp4",
    poster: "/images/video-poster.jpg",
    captions: "/captions/how-it-works.vtt",
};

const pickSrc = () =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
        ? VIDEO.srcMobile
        : VIDEO.src;

export const VIDEO_CHAPTERS = [
    { at: 0, label: "Intro", caption: "I'm Godwin, founder of AuditVanguard. Still running audits on spreadsheets and email? AuditVanguard puts your whole audit in one secure workspace." },
    { at: 8.8, label: "Connect your frameworks", caption: "Pick your frameworks, and the control baseline applies instantly." },
    { at: 12.2, label: "Map your controls", caption: "AI cross-maps every control and flags the gaps. Drop in evidence, and Magic Mapping matches it for you." },
    { at: 20.2, label: "Export audit-ready reports", caption: "Then export a branded, audit-ready report backed by a tamper-evident audit trail." },
    { at: 24.8, label: "Closing", caption: "From framework to sign-off — create your secure workspace at AuditVanguard.com." },
];

export const VideoSection = () => {
    const [started, setStarted] = useState(false);
    const [paused, setPaused] = useState(true);
    const [buffering, setBuffering] = useState(false);
    const [posterVisible, setPosterVisible] = useState(true);
    const [failed, setFailed] = useState(false);
    const [showTranscript, setShowTranscript] = useState(false);
    const videoRef = useRef(null);
    const srcRef = useRef(pickSrc());

    const play = () => {
        setStarted(true);
        setPaused(false);
        videoRef.current?.play();
    };

    const toggle = () => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) play();
        else videoRef.current.pause();
    };

    const seekTo = (t) => {
        if (failed || !videoRef.current) return;
        videoRef.current.currentTime = t;
        play();
    };

    const showPlayIcon = !failed && (!started || paused);
    const showSpinner = !failed && started && !paused && buffering;

    return (
        <section id="demo-video" className="max-w-7xl mx-auto px-5 sm:px-10 py-16 lg:py-24 border-t border-border/60 scroll-mt-20" data-testid="video-section">
            <div className="max-w-[960px] mx-auto">
                <h2 className="font-display text-2xl sm:text-3xl">See AuditVanguard in Action.</h2>
                <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                    A short walkthrough of how it works — from evidence to audit-ready report.
                </p>
                <div className="relative mt-6 rounded-2xl overflow-hidden border border-border/60 shadow-lg bg-black w-full aspect-video transform-none transition-none animate-none" style={{ aspectRatio: "16 / 9", minHeight: "157px" }} data-testid="video-player">
                    {failed ? (
                        <div className="w-full h-full grid place-items-center text-center p-6" data-testid="video-unavailable">
                            <div>
                                <div className="font-display font-semibold text-slate-100">Video unavailable</div>
                                <p className="text-sm text-slate-400 mt-1.5">Please refresh the page and try again.</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <video
                                ref={videoRef}
                                src={srcRef.current}
                                controls={started && paused}
                                preload="auto"
                                playsInline
                                onWaiting={() => setBuffering(true)}
                                onPlaying={() => { setBuffering(false); setPaused(false); }}
                                onCanPlay={() => setBuffering(false)}
                                onPause={() => setPaused(true)}
                                onEnded={() => setPaused(true)}
                                onLoadedData={() => setPosterVisible(false)}
                                onError={() => setFailed(true)}
                                className="w-full h-full"
                                data-testid="video-element"
                            >
                                <track default kind="captions" srcLang="en" label="English" src={VIDEO.captions} />
                            </video>
                            <img
                                src={VIDEO.poster}
                                alt=""
                                aria-hidden="true"
                                className={`absolute inset-0 w-full h-full object-cover pointer-events-none ${posterVisible ? "opacity-100" : "opacity-0"}`}
                                data-testid="video-poster-overlay"
                            />
                            {started && (
                                <button
                                    type="button"
                                    onClick={toggle}
                                    aria-label={paused ? "Play video" : "Pause video"}
                                    className={`absolute inset-x-0 top-0 cursor-pointer focus-visible:outline-none ${paused ? "bottom-14" : "bottom-0"}`}
                                    data-testid="video-tap-toggle"
                                />
                            )}
                            {showPlayIcon && (
                                <button
                                    type="button"
                                    onClick={play}
                                    aria-label="Play video: How AuditVanguard works"
                                    className={`group absolute focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${started ? "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full" : "inset-0 grid place-items-center"}`}
                                    data-testid="video-play-btn"
                                >
                                    <span className="h-16 w-16 rounded-full bg-accent text-accent-foreground grid place-items-center shadow-lg">
                                        <Play className="h-7 w-7 ml-1" strokeWidth={1.5} fill="currentColor" />
                                    </span>
                                </button>
                            )}
                            {showSpinner && (
                                <span className="absolute inset-0 grid place-items-center pointer-events-none" data-testid="video-buffer-spinner">
                                    <Loader2 className="h-10 w-10 text-slate-100 animate-spin" strokeWidth={1.5} />
                                </span>
                            )}
                        </>
                    )}
                </div>
                <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Video chapters" data-testid="video-chapters">
                    {VIDEO_CHAPTERS.map((s) => (
                        <button
                            key={s.at}
                            type="button"
                            onClick={() => seekTo(s.at)}
                            data-testid={`video-chapter-${Math.floor(s.at)}`}
                            className="min-h-[44px] inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-4 text-sm font-medium text-muted-foreground hover:text-foreground hover:border-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                            <span className="mono-label text-xs" aria-hidden="true">{fmt(s.at)}</span>
                            <span>{s.label}</span>
                        </button>
                    ))}
                </div>
                <div className="mt-4">
                    <button
                        type="button"
                        onClick={() => setShowTranscript((v) => !v)}
                        aria-expanded={showTranscript}
                        aria-controls="demo-transcript"
                        className="text-sm underline underline-offset-4 text-muted-foreground hover:text-foreground transition-colors tap-target"
                        data-testid="demo-transcript-toggle"
                    >
                        {showTranscript ? "Hide transcript" : "Read transcript"}
                    </button>
                    <div id="demo-transcript" hidden={!showTranscript} data-testid="demo-transcript">
                        <h3 className="sr-only">Demo narration transcript</h3>
                        <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
                            {VIDEO_CHAPTERS.map((s) => (
                                <li key={s.at} className="flex gap-3">
                                    <span className="mono-label shrink-0 w-12" aria-hidden="true">{fmt(s.at)}</span>
                                    <span>
                                        <span className="font-medium text-foreground">{s.label}.</span> {s.caption}
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>
        </section>
    );
};
